import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Percent, Film, Users, Flame, Plus, Trash2, Save, Calendar as CalIcon, Database, Download, Upload } from "lucide-react";

const TABS = [
  { v: "pricing", l: "Preistabellen", icon: Percent },
  { v: "series", l: "Filmreihen", icon: Film },
  { v: "shifts", l: "Schichtplan", icon: Users },
  { v: "heatmap", l: "Sitz-Heatmap", icon: Flame },
  { v: "calendar", l: "Kalender", icon: CalIcon },
  { v: "backup", l: "Backup & Restore", icon: Database },
];
const WEEKDAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
const TICKET_TYPES = ["", "Standard", "Kind", "Senior", "VIP", "Studenten", "Familie"];

export default function Betrieb() {
  const [tab, setTab] = useState("pricing");
  const Icon = TABS.find((t) => t.v === tab)?.icon || Percent;
  return (
    <div className="space-y-6" data-testid="betrieb-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Betrieb</div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl flex items-center gap-3"><Icon size={28} className="text-[#D4AF37]" /> {TABS.find((t) => t.v === tab)?.l}</h1>
      </div>
      <div className="flex flex-wrap gap-2 border-b border-[#1A1A1A] pb-2">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`tab-${t.v}`}
            className={`px-4 py-2 rounded-t text-[11px] uppercase tracking-widest flex items-center gap-2 ${tab === t.v ? "bg-[#D4AF37] text-black" : "text-[#A8A398] hover:text-[#D4AF37]"}`}>
            <t.icon size={13} /> {t.l}
          </button>
        ))}
      </div>
      {tab === "pricing" && <PricingRules />}
      {tab === "series" && <Series />}
      {tab === "shifts" && <Shifts />}
      {tab === "heatmap" && <Heatmap />}
      {tab === "calendar" && <CalendarWidget />}
      {tab === "backup" && <BackupRestore />}
    </div>
  );
}

function PricingRules() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", weekday: null, hour_from: null, hour_to: null, event_type: "", ticket_type: "", discount_percent: 20, active: true });
  const load = () => api.get("/pricing-rules").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name.trim()) return toast.error("Name fehlt");
    try { await api.post("/pricing-rules", form); toast.success("Regel angelegt"); setForm({ ...form, name: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (window.confirm("Regel löschen?")) { await api.delete(`/pricing-rules/${id}`); load(); } };
  const toggle = async (r) => { await api.patch(`/pricing-rules/${r.id}`, { ...r, active: !r.active }); load(); };

  return (
    <div className="space-y-4">
      <div className="card-lux p-5 grid md:grid-cols-7 gap-3 items-end">
        <div className="md:col-span-2">
          <L>Name</L>
          <I value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="z.B. Studenten-Mittwoch" testid="rule-name" />
        </div>
        <div>
          <L>Wochentag</L>
          <select value={form.weekday ?? ""} onChange={(e) => setForm({ ...form, weekday: e.target.value === "" ? null : Number(e.target.value) })} className={IN}>
            <option value="">Alle</option>
            {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        </div>
        <div><L>Ab (Uhr)</L><I type="number" value={form.hour_from ?? ""} onChange={(v) => setForm({ ...form, hour_from: v === "" ? null : Number(v) })} /></div>
        <div><L>Bis (Uhr)</L><I type="number" value={form.hour_to ?? ""} onChange={(v) => setForm({ ...form, hour_to: v === "" ? null : Number(v) })} /></div>
        <div>
          <L>Ticket-Typ</L>
          <select value={form.ticket_type} onChange={(e) => setForm({ ...form, ticket_type: e.target.value })} className={IN}>
            {TICKET_TYPES.map((t) => <option key={t} value={t}>{t || "Alle"}</option>)}
          </select>
        </div>
        <div><L>Rabatt %</L><I type="number" value={form.discount_percent} onChange={(v) => setForm({ ...form, discount_percent: parseFloat(v || 0) })} testid="rule-percent" /></div>
        <button onClick={create} data-testid="rule-create"
          className="md:col-span-7 bg-[#D4AF37] text-black py-2 rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82] flex items-center justify-center gap-2">
          <Plus size={14} /> Regel anlegen
        </button>
      </div>
      <div className="card-lux overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-[#A8A398] text-[10px] uppercase tracking-widest">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Tag</th><th className="text-left p-3">Zeit</th><th className="text-left p-3">Ticket</th><th className="text-right p-3">Rabatt</th><th className="text-center p-3">Aktiv</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#141414]" data-testid={`rule-row-${r.id}`}>
                <td className="p-3 text-[#F5F0E6]">{r.name}</td>
                <td className="p-3 text-[#A8A398]">{r.weekday !== null && r.weekday !== undefined ? WEEKDAYS[r.weekday] : "Jeder Tag"}</td>
                <td className="p-3 font-mono text-[#A8A398]">{r.hour_from != null || r.hour_to != null ? `${r.hour_from ?? 0}–${r.hour_to ?? 24}` : "—"}</td>
                <td className="p-3 text-[#A8A398]">{r.ticket_type || "Alle"}</td>
                <td className="p-3 text-right font-mono text-[#D4AF37]">− {r.discount_percent}%</td>
                <td className="p-3 text-center">
                  <button onClick={() => toggle(r)} className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${r.active ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-[#2A2A2A] text-[#575148]"}`}>
                    {r.active ? "Aktiv" : "Aus"}
                  </button>
                </td>
                <td className="p-3 text-right"><button onClick={() => del(r.id)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={12} /></button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="p-8 text-center text-[#A8A398]">Keine Preistabellen — automatische Rabatte an der Kasse werden hier gepflegt.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Series() {
  const [rows, setRows] = useState([]);
  const [movies, setMovies] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", poster_url: "", movie_ids: [], color: "#D4AF37" });
  const load = () => { api.get("/series").then((r) => setRows(r.data)); api.get("/movies").then((r) => setMovies(r.data || [])); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name.trim()) return toast.error("Name fehlt");
    try { await api.post("/series", form); toast.success("Filmreihe angelegt"); setForm({ name: "", description: "", poster_url: "", movie_ids: [], color: "#D4AF37" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (window.confirm("Filmreihe löschen?")) { await api.delete(`/series/${id}`); load(); } };
  const toggle = (mid) => setForm((f) => ({ ...f, movie_ids: f.movie_ids.includes(mid) ? f.movie_ids.filter((x) => x !== mid) : [...f.movie_ids, mid] }));

  return (
    <div className="space-y-4">
      <div className="card-lux p-5 space-y-3">
        <div className="grid md:grid-cols-3 gap-3">
          <div><L>Name</L><I value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Marvel Cinematic Universe" testid="series-name" /></div>
          <div><L>Poster URL</L><I value={form.poster_url} onChange={(v) => setForm({ ...form, poster_url: v })} placeholder="https://…" /></div>
          <div><L>Farbe</L><I type="color" value={form.color} onChange={(v) => setForm({ ...form, color: v })} /></div>
        </div>
        <div><L>Beschreibung</L><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className={IN} /></div>
        <div>
          <L>Filme in der Reihe</L>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 max-h-56 overflow-y-auto p-2 border border-[#1A1A1A] rounded">
            {movies.map((m) => (
              <label key={m.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${form.movie_ids.includes(m.id) ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A]"}`}>
                <input type="checkbox" checked={form.movie_ids.includes(m.id)} onChange={() => toggle(m.id)} className="accent-[#D4AF37]" />
                <span className="text-xs truncate">{m.title_de || m.title_original}</span>
              </label>
            ))}
          </div>
        </div>
        <button onClick={create} data-testid="series-create"
          className="w-full bg-[#D4AF37] text-black py-2 rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82]"><Plus size={14} className="inline mr-1" /> Filmreihe anlegen</button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((s) => (
          <div key={s.id} className="card-lux p-4" style={{ borderTop: `3px solid ${s.color || "#D4AF37"}` }} data-testid={`series-card-${s.id}`}>
            <div className="flex items-start gap-3">
              {s.poster_url && <img src={s.poster_url} alt="" className="w-14 h-20 object-cover rounded" />}
              <div className="flex-1 min-w-0">
                <div className="font-heading text-xl leading-tight">{s.name}</div>
                <div className="text-xs text-[#A8A398] mt-1">{s.movie_ids?.length || 0} Filme</div>
              </div>
              <button onClick={() => del(s.id)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={12} /></button>
            </div>
            {s.description && <div className="mt-3 text-sm text-[#A8A398]">{s.description}</div>}
          </div>
        ))}
        {!rows.length && <div className="col-span-full text-center py-12 text-[#A8A398]">Keine Filmreihen — Marvel, LOTR, Halloween-Nights einfach anlegen und gruppieren.</div>}
      </div>
    </div>
  );
}

function Shifts() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ user_id: "", date: new Date().toISOString().slice(0, 10), time_from: "18:00", time_to: "23:00", role: "kasse", notes: "" });
  const load = () => { api.get("/shifts").then((r) => setRows(r.data || [])); api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.user_id) return toast.error("Bitte Mitarbeiter wählen");
    try { await api.post("/shifts", form); toast.success("Schicht eingetragen"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/shifts/${id}`); load(); };

  const byDate = useMemo(() => {
    const m = {};
    for (const s of rows) (m[s.date] = m[s.date] || []).push(s);
    return Object.entries(m).sort();
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="card-lux p-5 grid md:grid-cols-6 gap-3 items-end">
        <div>
          <L>Mitarbeiter</L>
          <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className={IN} data-testid="shift-user">
            <option value="">Wählen …</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
          </select>
        </div>
        <div><L>Datum</L><I type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} /></div>
        <div><L>Von</L><I type="time" value={form.time_from} onChange={(v) => setForm({ ...form, time_from: v })} /></div>
        <div><L>Bis</L><I type="time" value={form.time_to} onChange={(v) => setForm({ ...form, time_to: v })} /></div>
        <div>
          <L>Rolle</L>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={IN}>
            <option value="kasse">Kasse</option>
            <option value="snack">Snackbar</option>
            <option value="admin">Betriebsleitung</option>
          </select>
        </div>
        <button onClick={create} data-testid="shift-create" className="bg-[#D4AF37] text-black py-2 rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82]"><Plus size={14} className="inline mr-1" /> Anlegen</button>
      </div>
      <div className="space-y-4">
        {byDate.map(([date, list]) => {
          const d = new Date(date + "T00:00:00");
          return (
            <div key={date} className="card-lux p-4">
              <div className="flex items-baseline gap-3 border-b border-[#1A1A1A] pb-2 mb-2">
                <div className="font-heading text-xl text-[#D4AF37]">{d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit" })}</div>
                <div className="text-xs text-[#575148]">{list.length} Schicht{list.length > 1 ? "en" : ""}</div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                {list.map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-2 border border-[#1A1A1A] rounded" data-testid={`shift-${s.id}`}>
                    <div>
                      <div className="font-mono text-sm">{s.time_from} – {s.time_to}</div>
                      <div className="text-xs">{s.user?.name || "?"} <span className="text-[9px] uppercase tracking-widest text-[#575148] ml-1">{s.role}</span></div>
                    </div>
                    <button onClick={() => del(s.id)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {!rows.length && <div className="card-lux p-8 text-center text-[#A8A398]">Keine Schichten geplant. Trag oben Mitarbeiter + Zeit ein.</div>}
      </div>
    </div>
  );
}

function Heatmap() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  useEffect(() => { api.get(`/stats/seat-heatmap?days=${days}`).then((r) => setData(r.data)); }, [days]);
  if (!data) return <div className="text-[#A8A398]">Lade …</div>;
  const color = (pct) => `rgba(212, 175, 55, ${0.15 + pct * 0.85})`;
  // Reihen sortieren
  const byRow = {};
  for (const s of data.seats) {
    const row = (s.seat.match(/^([A-Z]+)/) || ["", "?"])[1];
    (byRow[row] = byRow[row] || []).push(s);
  }
  const rows = Object.keys(byRow).sort();
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap card-lux p-4">
        <L inline>Zeitraum</L>
        {[30, 90, 180, 365].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`text-[11px] uppercase tracking-widest px-3 py-1 rounded ${days === d ? "bg-[#D4AF37] text-black" : "border border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/50"}`} data-testid={`heat-days-${d}`}>{d} Tage</button>
        ))}
        <div className="ml-auto text-xs text-[#A8A398]">
          {data.total_tickets} Tickets · Beliebtester Platz: {data.seats[0]?.seat || "—"} ({data.seats[0]?.count || 0}×)
        </div>
      </div>
      <div className="card-lux p-6">
        <div className="mx-auto max-w-xl border-b-2 border-[#D4AF37]/50 pb-2 text-center text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Leinwand</div>
        <div className="mt-8 space-y-2" data-testid="heatmap-grid">
          {rows.map((row) => (
            <div key={row} className="flex items-center gap-2 justify-center">
              <div className="w-6 text-right text-[10px] uppercase tracking-widest text-[#575148]">{row}</div>
              {byRow[row].sort((a, b) => a.seat.localeCompare(b.seat, undefined, { numeric: true })).map((s) => (
                <div key={s.seat} title={`${s.seat}: ${s.count}× gebucht`}
                  className="w-10 h-10 rounded flex items-center justify-center text-[10px] font-mono border border-[#2A2A2A]"
                  style={{ background: color(s.pct) }}>
                  {s.count}
                </div>
              ))}
            </div>
          ))}
          {!rows.length && <div className="text-center py-12 text-[#A8A398]">Noch keine Ticket-Daten im Zeitraum</div>}
        </div>
      </div>
    </div>
  );
}

function CalendarWidget() {
  const [shows, setShows] = useState([]);
  const [monthOffset, setMonthOffset] = useState(0);
  useEffect(() => { api.get("/showtimes?upcoming=false").then((r) => setShows(r.data || [])); }, []);
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const monthLabel = base.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  const daysInMonth = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  const firstDay = (base.getDay() + 6) % 7; // Mo=0
  const byDate = useMemo(() => {
    const m = {};
    for (const s of shows) (m[s.date] = m[s.date] || []).push(s);
    return m;
  }, [shows]);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ d, iso, shows: byDate[iso] || [] });
  }
  return (
    <div className="card-lux p-6" data-testid="calendar-widget">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <button onClick={() => setMonthOffset((m) => m - 1)} className="text-[#D4AF37] px-3 py-1 border border-[#D4AF37]/40 rounded">←</button>
        <div className="font-heading text-2xl">{monthLabel}</div>
        <button onClick={() => setMonthOffset((m) => m + 1)} className="text-[#D4AF37] px-3 py-1 border border-[#D4AF37]/40 rounded">→</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest text-[#575148] mb-2">
        {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => <div key={d} className="text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => c ? (
          <div key={i} className={`aspect-square border rounded p-1 flex flex-col ${c.shows.length ? "border-[#D4AF37]/50 bg-[#D4AF37]/[0.04]" : "border-[#1A1A1A]"}`}>
            <div className="text-xs text-[#F5F0E6]">{c.d}</div>
            <div className="flex-1 flex flex-wrap gap-0.5 mt-1 overflow-hidden">
              {c.shows.slice(0, 3).map((s) => (
                <div key={s.id} title={`${s.start_time} · ${s.hall}`} className="text-[8px] font-mono px-1 rounded bg-[#D4AF37]/20 text-[#D4AF37]">{s.start_time}</div>
              ))}
              {c.shows.length > 3 && <div className="text-[8px] text-[#D4AF37]">+{c.shows.length - 3}</div>}
            </div>
          </div>
        ) : <div key={i} />)}
      </div>
    </div>
  );
}

const IN = "w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]";
function L({ children, inline }) { return <label className={`text-[10px] uppercase tracking-widest text-[#A8A398] ${inline ? "" : "block"}`}>{children}</label>; }
function I({ value, onChange, type = "text", placeholder, testid }) {
  return <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className={IN} />;
}

function BackupRestore() {
  const [summary, setSummary] = useState(null);
  const [mode, setMode] = useState("merge");
  const [busy, setBusy] = useState(false);
  const [lastRestore, setLastRestore] = useState(null);

  const load = () => api.get("/admin/backup/summary").then((r) => setSummary(r.data));
  useEffect(() => { load(); }, []);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `movie-house-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup heruntergeladen");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backup fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (mode === "replace" && !window.confirm("REPLACE-Modus überschreibt bestehende Daten. Fortfahren?")) return;
    setBusy(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const res = await api.post("/admin/restore", { collections: payload.collections, mode });
      setLastRestore(res.data);
      toast.success(`Restore erfolgreich (${mode})`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore fehlgeschlagen: " + (e.message || ""));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="backup-page">
      <div className="card-lux p-5 space-y-3">
        <div className="flex items-center gap-2 text-[#D4AF37]"><Database size={18} /><span className="font-heading text-xl">Datenbank-Snapshot</span></div>
        <p className="text-sm text-[#A8A398]">Lade eine vollständige JSON-Sicherung aller Filme, Vorstellungen, Tickets, Bestellungen und Einstellungen herunter. Auf einem sicheren Ort verwahren.</p>
        <button onClick={download} disabled={busy} data-testid="backup-download"
          className="bg-[#D4AF37] text-black py-2 px-5 rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82] flex items-center gap-2 disabled:opacity-50">
          <Download size={14} /> Backup herunterladen
        </button>
      </div>

      {summary && (
        <div className="card-lux p-5">
          <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-3">Aktueller Datenbestand</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(summary.counts).map(([k, v]) => (
              <div key={k} className="border border-[#1A1A1A] rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#575148]">{k}</div>
                <div className="font-mono text-2xl text-[#D4AF37]">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-lux p-5 space-y-3">
        <div className="flex items-center gap-2 text-[#D4AF37]"><Upload size={18} /><span className="font-heading text-xl">Restore aus Backup</span></div>
        <p className="text-sm text-[#A8A398]">Wähle eine zuvor exportierte JSON-Datei. Im <span className="text-[#EEDC82]">Merge</span>-Modus werden Datensätze per ID aktualisiert. <span className="text-[#7A1921]">Replace</span> löscht bestehende Collections zuvor.</p>
        <div className="flex flex-wrap gap-2 items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={mode === "merge"} onChange={() => setMode("merge")} className="accent-[#D4AF37]" data-testid="mode-merge" />
            <span className="text-sm">Merge (empfohlen)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} className="accent-[#7A1921]" data-testid="mode-replace" />
            <span className="text-sm text-[#7A1921]">Replace (überschreibt alles)</span>
          </label>
        </div>
        <label className={`inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] py-2 px-5 rounded text-[11px] uppercase tracking-widest cursor-pointer hover:bg-[#D4AF37]/10 ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          <Upload size={14} /> Backup-Datei wählen
          <input type="file" accept="application/json,.json" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid="backup-upload" />
        </label>
        {lastRestore && (
          <div className="mt-3 text-xs text-[#A8A398] font-mono border border-[#D4AF37]/30 bg-[#D4AF37]/[0.04] p-3 rounded" data-testid="restore-report">
            Wiederhergestellt ({lastRestore.mode}):{" "}
            {Object.entries(lastRestore.restored).map(([k, v]) => `${k}=${v}`).join(", ")}
          </div>
        )}
      </div>
    </div>
  );
}
